import SendIcon from '@mui/icons-material/Send';
import { LoadingButton } from '@mui/lab';
import { Box, Stack, Typography, Theme } from '@mui/material';
import { useFormik } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IPhoneForInstructionProps } from './types';
import { AuthService } from '../../../../../services/auth/AuthService';

import ErrorHandler from '../../../../../common/utils/errorHandler';
import { BackButton } from '../../../../../components/common/Button/BackButton/BackButton';

import CheckIcon from '../../../../../assets/icons/updatePasswordIcon.svg?react';
import { LoginTabMethod } from '../../../../../components/forms/LoginForm/LoginTabMethod';
import { signInValidationSchema } from '../../../../../components/forms/LoginForm/signInValidationSchema';
import { useUserActions } from '../../../../../hooks/useUser';
import { ROUTES } from '../../../../../constants/routes';
import { useNavigate } from 'react-router-dom';

const authService = new AuthService();

export const PhoneForInstructionForm: React.FC<IPhoneForInstructionProps> = ({
  setActiveTab,
  setIsForgotPassword,
}) => {
  const [serverError, setServerError] = useState<string | null>(null);

  const { t } = useTranslation();
  const { setUserPhoneOrEmail } = useUserActions();
  const navigate = useNavigate();

  const errorHandler = new ErrorHandler(t, {
    notFoundError: t('userAuth.recoverPassword.validationErrors.notFound'),
  });
  const {
    values,
    handleSubmit,
    isSubmitting,
    touched,
    errors,
    setFieldTouched,
    setFieldValue,
    resetForm,
    handleChange,
  } = useFormik({
    initialValues: {
      phone: '',
      email: '',
    },
    validationSchema: signInValidationSchema(t),
    initialTouched: {
      phone: false,
      email: false,
    },
    onSubmit: async (formValues) => {
      setServerError('');
      try {
        let data: Record<string, string> = {};

        for (const key in formValues) {
          if (formValues[key as keyof typeof formValues]) {
            data = {
              [key as keyof typeof formValues]:
                formValues[key as keyof typeof formValues],
            };
          }
        }

        await authService.sendResetPasswordInstruction(data);
        setUserPhoneOrEmail(data);
        navigate(`/${ROUTES.RESET_PASSWORD}`);
        resetForm();
      } catch (error) {
        const handledError = errorHandler.handleError(error);
        if ('server' in handledError) setServerError(handledError.server!);
      }
    },
  });

  const handleChangePhone = (newValue: string) => {
    setFieldValue('phone', newValue, true);
  };

  return (
    <>
      <Stack
        sx={{
          position: 'absolute',
          top: '6%',
          left: '22px',
          zIndex: 1,
        }}
      >
        <BackButton
          skipReloadPage
          customClickFn={() => setIsForgotPassword(false)}
        />
      </Stack>
      <Box
        sx={{
          height: '100%',
          width: '100%',
          flex: '1 1 auto',
          display: 'grid',
          placeContent: 'center',
          backdropFilter: 'blur(5px)',
        }}
      >
        <CheckIcon />
      </Box>
      <Stack
        textAlign="center"
        sx={(theme: Theme) => ({
          padding: { xs: '20px 12px' },
          gap: { xs: '25px' },
          background: theme.palette.gray[300],
          boxShadow: `0px -32px 30px 15px ${theme.palette.gray[300]}`,
        })}
      >
        <Typography variant="h1" color="green.main">
          {t('userAuth.recoverPassword.sendEmailInstructionTitle')}
        </Typography>
        <Typography variant="body1" color="common.white">
          {t('userAuth.recoverPassword.sendEmailInstructionText')}
        </Typography>
        <form
          onSubmit={handleSubmit}
          style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}
        >
          <LoginTabMethod
            errors={errors}
            handleChange={handleChange}
            handleChangePhone={handleChangePhone}
            setFieldTouched={setFieldTouched}
            touched={touched}
            values={values}
          />
          <Box
            sx={{
              position: 'relative',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              justifyContent: 'space-between',
              gap: { xs: '30px' },
            }}
          >
            <BackButton
              setActiveTab={setActiveTab}
              setIsForgotPassword={setIsForgotPassword}
              text={`${t('orderPage.back')}`}
            />
            <LoadingButton
              variant="contained"
              color="primary"
              type="submit"
              endIcon={!isSubmitting ? null : <SendIcon />}
              loading={isSubmitting}
            >
              {t('userAuth.recoverPassword.sendEmailInstructionBtn')}
            </LoadingButton>
            {serverError ? (
              <Typography
                variant="body1"
                color={'error'}
                sx={{
                  position: 'absolute',
                  top: '-70%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '100%',
                }}
              >
                {serverError}
              </Typography>
            ) : null}
          </Box>
        </form>
      </Stack>
    </>
  );
};
